import { Node } from '@babylonjs/core/node';
import { Scene } from '@babylonjs/core/scene';
import { GLTF2Export, GLTFData, IExportOptions } from '@babylonjs/serializers/glTF';
import { Connector, ViewpointNode } from '@kb3d/models/classes';
import { getByDynamicId } from '@kb3d/models/classes/kb3d-manager';

export class GLTFKb3dExporter {
    constructor(private scene: Scene) {}

    /** generates the current scene into a glb file and returns an object url for it */
    public generateGlb(filename = 'scene'): Promise<Blob> {
        return GLTF2Export.GLBAsync(this.scene, filename, this._glbExportOptions).then(glbData => {
            return this.createGlbBlob(glbData);
        });
    }

    public exportGlb(filename = 'scene') {
        return GLTF2Export.GLBAsync(this.scene, filename, this._glbExportOptions).then(glbData => {
            const fileNameWithExt = `${filename}.glb`;
            const dispose = this.downloadBlob(fileNameWithExt, this.createGlbBlob(glbData));
            setTimeout(() => dispose());
            return { fileName: fileNameWithExt };
        });
    }

    public exportGltf(filename = 'scene') {
        return GLTF2Export.GLTFAsync(this.scene, filename, this._glbExportOptions).then(glbData => {
            const disposeFuncs: Array<() => void> = [];
            for (const key in glbData.glTFFiles) {
                const blob = glbData.glTFFiles[key];
                let mimeType;
                if (endsWith(key, '.glb')) {
                    mimeType = { type: 'model/gltf-binary' };
                } else if (endsWith(key, '.bin')) {
                    mimeType = { type: 'application/octet-stream' };
                } else if (endsWith(key, '.gltf')) {
                    mimeType = { type: 'model/gltf+json' };
                } else if (endsWith(key, '.jpeg') || endsWith(key, '.jpg')) {
                    mimeType = { type: 'image/jpeg' /* ImageMimeType.JPEG */ };
                } else if (endsWith(key, '.png')) {
                    mimeType = { type: 'image/png' /* ImageMimeType.PNG */ };
                }

                disposeFuncs.push(this.downloadBlob(key, new Blob([blob], mimeType)));
            }
            setTimeout(() => disposeFuncs.forEach(dispose => dispose()));
        });

        function endsWith(str: string, suffix: string) {
            return str.indexOf(suffix, str.length - suffix.length) !== -1;
        }
    }

    private _glbExportOptions: IExportOptions = {
        shouldExportNode: n => this.shouldExportNode(n),
    };

    protected shouldExportNode(node: Node) {
        const kb3dNode = getByDynamicId(node.id);
        if (kb3dNode instanceof Connector) {
            return false;
        } else if (kb3dNode instanceof ViewpointNode) {
            return false;
        }
        return kb3dNode != null;
    }

    protected createGlbBlob(glbData: GLTFData) {
        //we're expecting one file since it's a GLB
        const key = Object.keys(glbData.glTFFiles)[0];
        const blob = glbData.glTFFiles[key];
        const mimeType = { type: 'model/gltf-binary' };
        return new Blob([blob], mimeType);
    }

    protected downloadBlob(fileName: string, blob: Blob) {
        const link = document.createElement('a');
        document.body.appendChild(link);
        link.setAttribute('type', 'hidden');
        link.download = fileName;
        link.href = window.URL.createObjectURL(blob);
        link.click();
        return dispose;

        function dispose() {
            window.URL.revokeObjectURL(link.href);
            link.remove();
        }
    }
}
